class StyleManager {
    constructor() {
        this.defaultStyle = {
            strokeWeight: 1.5,
            fillColor: '#ff0000',
            fillOpacity: 0,
            strokeColor: '#cc0000',
            visible: false,
            clickable: false
        };

        this.selectedStyle = {
            strokeWeight: 1.5,
            fillOpacity: 0.4,
            fillColor: '#4caf50',
            strokeColor: '#2ecc71',
            clickable: true,
            visible: true,
            zIndex: 3  // Lägre z-index för tidigare selection types (gröna)
        };

        this.selectedCurrentTypeStyle = {
            strokeWeight: 1.5,
            fillOpacity: 0.12,
            fillColor: '#ff0000',
            strokeColor: '#cc0000',
            clickable: true,
            visible: true,
            zIndex: 4  // Högre z-index för nuvarande selection type (rosa) - ska alltid vara överst
        };

        this.salesDistrictStyle = {
            strokeWeight: 0.5,
            fillOpacity: 0.5,
            clickable: true, // Måste vara true för att mouseover ska fungera
            visible: true,
            zIndex: 1
        };

        this.mapLayerStyle = {
            visible: true,
            zIndex: 1
        };

        this.clickAreaListener = null;
        this.terraDrawStyleFn = null;
        this.isDrawing = false;
    }

    setTerraDrawStyleFn(fn) {
        this.terraDrawStyleFn = fn;
    }

    getTerraDrawStyle(feature) {
        if (this.terraDrawStyleFn) {
            try {
                return this.terraDrawStyleFn(feature);
            } catch {
                return {};
            }
        }
        return {};
    }

    renderGeoJSON(featureCollection, map) {
        map.data.addGeoJson(featureCollection);
        
        const defaultStyle = { 
            strokeWeight: 1.5, 
            fillColor: '#ff0000', 
            fillOpacity: 0, 
            strokeColor: '#cc0000',
            visible: true,
            clickable: true
        };
        
        map.data.setStyle((feature) => {
            if (!feature.getProperty('type')) {
                return this.getTerraDrawStyle(feature);
            }
            return defaultStyle;
        });
    }

    attachAreaClickEvent(map, maps, onAreaClick) {
        // Remove existing listener if any
        if (this.clickAreaListener) {
            maps.event.removeListener(this.clickAreaListener);
            this.clickAreaListener = null;
        }

        // Add new listener
        this.clickAreaListener = map.data.addListener('click', (event) => {
            if (this.isDrawing) return;

            const feature = event.feature;
            if (!feature) {
                return;
            }

            const featureId = feature.getProperty('id');
            const featureType = feature.getProperty('type');
            
            // Only handle clicks on features of the current selection type
            if (featureType && !featureType.startsWith('maplayer-')) {
                onAreaClick(featureId);
            }
        });

        return this.clickAreaListener;
    }

    removeAreaClickEvent(maps) {
        if (this.clickAreaListener) {
            maps.event.removeListener(this.clickAreaListener);
            this.clickAreaListener = null;
        }
    }

    changeSelection(map, type) {
        // First, make all features of the current selection type invisible and unclickable
        map.data.forEach((feature) => {
            const featureType = feature.getProperty('type');
            if (!featureType) return;
            
            // Only modify features that are not layer toggle features
            if (!featureType.startsWith('maplayer-')) {
                map.data.overrideStyle(feature, { 
                    visible: false,
                    clickable: false
                });
            }
        });

        // Then, make features of current selection type visible and clickable
        map.data.forEach((feature) => {
            const featureType = feature.getProperty('type');
            if (!featureType) return;
            if (featureType === type) {
                map.data.overrideStyle(feature, { 
                    visible: true,
                    clickable: true,
                    zIndex: 3,  // Higher z-index than layer toggle features
                    strokeWeight: 1.5,
                    fillColor: '#ff0000',
                    fillOpacity: 0,
                    strokeColor: '#cc0000'
                });
            }
        });
    }

    applyStyles({ map, selectedAreas, selectionType, currentDistrictId = null, currentDistrictName = null }) {
        if (!map) return;
       
        // Skapa Set för O(1) lookup istället för O(n) some() check
        const selectedAreaIds = new Set();
        if (selectedAreas && selectedAreas.length > 0) {
            selectedAreas.forEach(area => {
                if (area && area.properties && area.properties.id) {
                    selectedAreaIds.add(area.properties.id);
                }
            });
        }

        // Apply styles in a single loop instead of two
        map.data.forEach((feature) => {
            const type = feature.getProperty('type');
            if (!type) {
                return;
            }
            const featureId = feature.getProperty('id');
            const isSelected = selectedAreaIds.has(featureId);
            const isSalesDistrict = type === 'salesdistrict';
            const isMapLayer = type.indexOf('maplayer-') > -1;
            
            if (isSalesDistrict) {
                // Hoppa över om detta är det nuvarande distriktet (via districtId)
                const featureDistrictId = feature.getProperty('districtId');
                if (currentDistrictId && featureDistrictId) {
                    const currentIdStr = String(currentDistrictId).trim();
                    const districtIdStr = String(featureDistrictId).trim();
                    if (currentIdStr === districtIdStr || 
                        (Number(currentIdStr) && Number(districtIdStr) && Number(currentIdStr) === Number(districtIdStr))) {
                        // Detta är det nuvarande distriktet - hoppa över sales district styling
                        return;
                    }
                }
                this.applySalesDistrictStyle(feature, map);
                return;
            }

            if (isMapLayer) {
                this.applyMapLayerStyle(feature, map);
                return;
            }

            if (isSelected) {
                // If selected and type matches selectionType, use red style
                if (type === selectionType) {
                    this.applySelectedCurrentTypeStyle(feature, map);
                } else {
                    this.applySelectedStyle(feature, map);
                }
                return;
            }

            // For non-selected features, apply type-based style
            this.applyTypeBasedStyle(feature, map, type, selectionType);
        });
    }

    /**
     * Selektiv style-uppdatering - bara uppdatera features som faktiskt ändrats
     * Mycket snabbare för zip5 med många distrikt
     */
    applyStylesSelective({ map, selectedAreas, selectionType, changedFeatureIds, currentSelectedAreaIds, previousSelectedAreaIds, currentDistrictId = null, currentDistrictName = null }) {
        if (!map) return;

        // Bara uppdatera features som faktiskt ändrats
        map.data.forEach((feature) => {
            const featureId = feature.getProperty('id');
            const type = feature.getProperty('type');
            if (!type) {
                return;
            }
            
            // Hoppa över om feature inte ändrats
            if (!changedFeatureIds.has(featureId)) {
                return;
            }

            const isSelected = currentSelectedAreaIds.has(featureId);
            const isSalesDistrict = type === 'salesdistrict';
            const isMapLayer = type.indexOf('maplayer-') > -1;
            
            if (isSalesDistrict) {
                // TODO: När API:et stödjer id i spatialinfo, ändra till att matcha på districtId istället för name
                // Hoppa över om detta är det nuvarande distriktet - matcha på name tills API:et stödjer id
                const featureName = feature.getProperty('name');
                if (currentDistrictName && featureName && String(currentDistrictName).trim() === String(featureName).trim()) {
                    // Detta är det nuvarande distriktet - hoppa över sales district styling
                    return;
                }
                this.applySalesDistrictStyle(feature, map);
                return;
            }

            if (isMapLayer) {
                this.applyMapLayerStyle(feature, map);
                return;
            }

            if (isSelected) {
                // If selected and type matches selectionType, use red style
                if (type === selectionType) {
                    this.applySelectedCurrentTypeStyle(feature, map);
                } else {
                    this.applySelectedStyle(feature, map);
                }
                return;
            }

            // Feature blev avvald - återställ till default style för selection type
            this.applyTypeBasedStyle(feature, map, type, selectionType);
        });
    }

    applySalesDistrictStyle(feature, map) {
        map.data.overrideStyle(feature, {
            ...this.salesDistrictStyle,
            strokeColor: feature.getProperty('color'),
            fillColor: feature.getProperty('color'),
            clickable: true // Måste vara true för att mouseover ska fungera
        });
    }

    applyMapLayerStyle(feature, map) {
        map.data.overrideStyle(feature, this.mapLayerStyle);
    }

    applySelectedStyle(feature, map) {
        map.data.overrideStyle(feature, this.selectedStyle);
    }

    applySelectedCurrentTypeStyle(feature, map) {
        map.data.overrideStyle(feature, this.selectedCurrentTypeStyle);
    }

    applyTypeBasedStyle(feature, map, type, selectionType) {
        if (selectionType === 'clear') {
            map.data.overrideStyle(feature, {
                ...this.defaultStyle,
                visible: false,
                clickable: false
            });
        } else if (type === selectionType) {
            // If this is the current selection type, make it visible and clickable
            map.data.overrideStyle(feature, {
                ...this.defaultStyle,
                visible: true,
                clickable: true,
                zIndex: 100
            });
        } else {
            // For other types, make them invisible and unclickable
            map.data.overrideStyle(feature, {
                ...this.defaultStyle,
                visible: false,
                clickable: false
            });
        }
    }

    setDefaultStyle(map) {
        if (!map) return;
        const defaultStyle = this.defaultStyle;
        map.data.setStyle((feature) => {
            if (!feature.getProperty('type')) {
                return this.getTerraDrawStyle(feature);
            }
            return defaultStyle;
        });
    }
}

export default StyleManager; 